import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Pure helper: returns true if a query string indicates retro mode.
// Exported for testability (also used internally in the provider).
export function parseRetroFromSearch(search) {
  if (!search) return false;
  const trimmed = search.startsWith('?') ? search.slice(1) : search;
  if (!trimmed) return false;
  const params = new URLSearchParams(trimmed);
  if (!params.has('retro')) return false;
  const value = params.get('retro');
  // Bare ?retro flag (URLSearchParams returns '') is truthy.
  if (value === null || value === '') return true;
  const lower = value.toLowerCase();
  return lower === '1' || lower === 'true' || lower === 'yes';
}

const RetroContext = createContext({
  isRetro: false,
  setRetro: () => {},
});

const SS_KEY = 'retroMode';

export function RetroProvider({ children }) {
  const [isRetro, setIsRetroState] = useState(false);

  // After hydration: read sessionStorage OR URL param, flip if either is set.
  // sessionStorage means retro mode persists across page navigations within
  // the same tab session (clicking "Say hello" → /contact stays retro), but
  // closing the tab resets to modern. The URL param ?retro=1 is the
  // shareable deep-link override.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let next = false;
    try {
      next = window.sessionStorage.getItem(SS_KEY) === '1';
    } catch (e) {
      /* sessionStorage may be blocked (private mode); fall back to URL */
    }
    if (parseRetroFromSearch(window.location.search)) {
      next = true;
    }
    if (next) setIsRetroState(true);
  }, []);

  // Sync DOM attribute + sessionStorage whenever state changes.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isRetro) {
      document.documentElement.setAttribute('data-theme', 'retro');
      try { window.sessionStorage.setItem(SS_KEY, '1'); } catch (e) { /* ignore */ }
    } else {
      document.documentElement.removeAttribute('data-theme');
      try { window.sessionStorage.removeItem(SS_KEY); } catch (e) { /* ignore */ }
    }
  }, [isRetro]);

  // Wrap setter so toggling off also strips ?retro=1 from the URL,
  // preventing a refresh from re-enabling retro mode.
  const setRetro = useCallback((next) => {
    setIsRetroState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (!value && typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        if (url.searchParams.has('retro')) {
          url.searchParams.delete('retro');
          window.history.replaceState({}, '', url.toString());
        }
      }
      return value;
    });
  }, []);

  return (
    <RetroContext.Provider value={{ isRetro, setRetro }}>
      {children}
    </RetroContext.Provider>
  );
}

export function useRetro() {
  return useContext(RetroContext);
}
